import React, { useCallback } from "react";
import styled from "styled-components";
import { Button, Flex, Text, Asset } from "@thenounproject/lingo-core";

import AssetDownloadOptionThumbnail from "./AssetDownloadOptionThumbnail";
import ExportOption from "./ExportOption";
import { CopyAsset, DownloadAsset, IconStatus } from "@features/asset-export/types";

const MetaDetailWrapper = styled(Flex).attrs({
  height: "40px",
  flexDirection: "column",
  justifyContent: "center",
})``;

const Title = styled(Text).attrs({
  font: "ui.small",
  truncate: true,
  minWidth: "60px",
})``;
const Subtitle = styled(Text).attrs({
  font: "ui.small",
  truncate: true,
  minWidth: "60px",
  color: "grayDarker",
})``;

type Props = {
  asset: Asset;
  type: string;
  description: string;
  size: string;
  dpi: number;
  downloadAsset: DownloadAsset;
  copyAsset: CopyAsset;
  copyStatus?: { fileType: string; status: IconStatus };
  downloadStatus?: { fileType: string; status: Exclude<IconStatus, null> };
};

const PresetExportOption: React.FC<Props> = ({
  asset,
  type,
  description,
  size,
  dpi,
  downloadAsset,
  copyAsset,
  copyStatus,
  downloadStatus,
}) => {
  const onCopyAsset = () => {
    copyAsset("preset", type, size, asset, dpi);
  };

  const getCopyIcon = useCallback(() => {
    if (copyStatus?.fileType !== type) return "action.copy";
    if (copyStatus?.status === "success") return "info.checkmark";
    if (copyStatus?.status === "error") return "info.error";
    return "action.copy";
  }, [copyStatus, type]);

  const getDownloadIcon = useCallback(() => {
    if (downloadStatus?.fileType !== type) return "download";
    if (downloadStatus?.status === "error") {
      return "info.error";
    }
    if (downloadStatus?.status === "success") {
      return "info.checkmark";
    }
    return "download";
  }, [downloadStatus, type]);

  return (
    <ExportOption>
      <Flex justifyContent="space-between" width="100%" alignItems="center">
        <Flex>
          <AssetDownloadOptionThumbnail asset={asset} />
          <MetaDetailWrapper>
            <Title>
              {type} {description && <strong title={description}>{description}</strong>}
            </Title>
            <Subtitle color="grayDarker" font="ui.small">
              {size}
            </Subtitle>
          </MetaDetailWrapper>
        </Flex>
        <Flex gap="4px">
          {["PNG", "SVG"].includes(type) && (
            <Button
              id={`copy-button-${type.toLowerCase()}-${size}-${dpi || 0}`}
              aria-label={`Copy ${type} ${size}`}
              onClick={onCopyAsset}
              icon={getCopyIcon()}
              loading={type === copyStatus?.fileType && copyStatus?.status === "loading"}
              size="small"
              buttonStyle="secondary"
            />
          )}
          <Button
            id={`dl-button-${type.toLowerCase()}-${size}-${dpi || 0}`}
            aria-label={`Download ${type} ${size}`}
            loading={downloadStatus?.fileType === type && downloadStatus?.status === "loading"}
            onClick={() => downloadAsset("preset", type, size, dpi)}
            icon={getDownloadIcon()}
            size="small"
          />
        </Flex>
      </Flex>
    </ExportOption>
  );
};

export default PresetExportOption;
